import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/Layout/Layout';
import { RoutesLink } from '@/utility/RouteLinks';
import { IFormData, IFormRowData } from '@/interfaces/form.model';
import FilterComponent, {
  IFilterArray,
} from '@/components/common/FilterComponent';
import CommonTable, {
  ITableColumn,
  IActionData,
} from '@/components/common/CommonTable';
import CardLayout from '@/components/Layout/CardLayout';
import { FormService } from '@/services/FormService';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import EditIcon from '@/components/Icons/EditIcon';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import moment from 'moment';
import { showToastNotification } from '@/utility/helper';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { RootState } from '@/redux/store';

interface IPageStates {
  tableLoader: boolean;
  searchQuery: string;
}

const Forms = (): React.ReactNode => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<IFormRowData[]>([]);
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string | null;
  }>({
    isOpen: false,
    id: null,
  });
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const [pageStates, setPageStates] = useState<IPageStates>({
    tableLoader: false,
    searchQuery: '',
  });

  const fetchForms = useCallback(async () => {
    setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: true }));
    try {
      const filter = {
        pageNumber: filterDetails.currentPage,
        pageSize: filterDetails.pageSize,
        search: pageStates.searchQuery,
        orderBy: filterDetails.orderBy,
      };

      const response = await FormService.getForms(filter);
      console.log(response, 'response');

      if (response?.status) {
        const forms = response.data?.officeForm || [];
        const newData = forms?.map((officeForm: IFormData, idx: number) => ({
          ...officeForm,
          sno:
            (filterDetails.currentPage - 1) * (filterDetails.pageSize || 10) +
            idx +
            1,
          // Ensure basic fields are present for processedData mapping later
          name: officeForm?.name ?? '--',
          description: officeForm?.description ?? '--',
          totalColumns: officeForm?.columns?.length ?? 0,
          status: officeForm?.status ?? '--',
          createdAt: officeForm?.createdAt ?? '--',
        }));
        setTableData(newData as IFormRowData[]);
        dispatch(
          setUpdateFilterStates({
            count: response.data?.pageInfo?.total ?? 0,
            totalPages: response.data?.pageInfo?.totalPages ?? 1,
          }),
        );
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to fetch forms',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        (error as Error)?.message || 'Error fetching forms',
      );
    } finally {
      setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: false }));
    }
  }, [
    filterDetails.currentPage,
    filterDetails.pageSize,
    filterDetails.orderBy,
    pageStates.searchQuery,
    dispatch,
  ]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      dispatch(setUpdateFilterStates({ currentPage: 1 }));
      setPageStates((prevState: IPageStates) => ({
        ...prevState,
        searchQuery: value,
      }));
    }, 500);
  };

  const pageChangeHandler = (PageNo: number) => {
    if (PageNo > 0 && PageNo <= filterDetails?.totalPages) {
      dispatch(setUpdateFilterStates({ currentPage: PageNo }));
    }
  };

  const handleChangePageSize = (value: number) => {
    dispatch(setUpdateFilterStates({ pageSize: value, currentPage: 1 }));
  };

  const handleDelete = async () => {
    if (!deleteModal.id) return;
    try {
      const response = await FormService.deleteForm(deleteModal.id);
      if (response?.status) {
        showToastNotification('success', response.message);
        fetchForms();
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        (error as Error)?.message || 'Error deleting form',
      );
    } finally {
      setDeleteModal({ isOpen: false, id: null });
    }
  };

  const tableHeading: ITableColumn<IFormRowData>[] = [
    { label: 'S.No.', value: 'sno' },
    { label: 'Form Name', value: 'name' },
    // { label: 'Description', value: 'description' },
    { label: 'Total Columns', value: 'totalColumns' },
    { label: 'Status', value: 'formStatus' },
    { label: 'Created At', value: 'formCreatedAt' },
  ];

  const actionData: IActionData<IFormRowData>[] = [
    {
      label: 'View',
      className: 'btn btn-outline-secondary p-1 border-0',
      icon: <ViewIcon />,
      title: 'View',
      clickHandler: (data) => navigate(`${RoutesLink.formView}/${data.id}`),
    },
    {
      label: 'Edit',
      className: 'btn btn-success-light p-1 border-0',
      icon: <EditIcon />,
      title: 'Edit',
      clickHandler: (data) => navigate(`${RoutesLink.editForm}/${data.id}`),
    },
    {
      label: 'Delete',
      className: 'btn btn-danger-light p-1 border-0',
      icon: <DeleteIcon />,
      title: 'Delete',
      clickHandler: (data) => setDeleteModal({ isOpen: true, id: data.id }),
    },
  ];

  const filterArray: IFilterArray<boolean> = {
    isShow: false,
  };

  // Convert status to badge styling and format date
  const processedData: IFormRowData[] = tableData?.map((item) => {
    // Extract status string safely if it's an object
    const statusStr = item?.status;
    // console.log("statusStr", statusStr);

    let statusClass = 'bg-secondary';
    const lowerStatus = String(statusStr || '').toLowerCase();

    if (lowerStatus === 'published' || lowerStatus === 'active')
      statusClass = 'badge bg-green-25 fw-normal rounded-pill';
    if (lowerStatus === 'draft' || lowerStatus === 'pending')
      statusClass = 'badge bg-yellow-25 fw-normal rounded-pill';
    if (lowerStatus === 'archived' || lowerStatus === 'inactive')
      statusClass = 'bg-danger';

    return {
      ...item,
      formStatus: (
        <span className={`badge ${statusClass} fw-normal rounded-pill`}>
          {statusStr ? String(statusStr)?.toUpperCase() : 'N/A'}
        </span>
      ),
      formCreatedAt: item?.createdAt
        ? moment(item?.createdAt).format('DD MMM YYYY, hh:mm A')
        : '--',
    };
  });

  return (
    <Layout>
      <MainHeading
        title="Forms"
        buttonText="Create New Form"
        buttonLink={RoutesLink.formBuilder}
        buttonIcon={<i className="bi bi-plus-lg me-2"></i>}
      />
      <CardLayout>
        <FilterComponent
          searchBar={true}
          searchPlaceHolder="Search Forms"
          handleSearch={handleSearchChange}
          filterArray={filterArray}
        />
        <CommonTable
          tableData={processedData}
          tableHeading={tableHeading}
          count={filterDetails.count}
          pageSize={filterDetails.pageSize || 10}
          currentPage={filterDetails.currentPage}
          totalPages={filterDetails.totalPages}
          pageChangeHandler={pageChangeHandler}
          handleChangePageSize={handleChangePageSize}
          isLoading={pageStates.tableLoader}
          ActionData={actionData}
        />
      </CardLayout>
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        toggle={() => setDeleteModal({ isOpen: false, id: null })}
        submitHandler={handleDelete}
      />
    </Layout>
  );
};

export default Forms;
